import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CHECKLIST_ITEM, DESCRIPTION, NAME, TITLE, IMPORTANCE_FLAG } from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { schema, formValues } from "./validations";
import {
  capitalize,
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components";
import {
  useAddSafetyChecklistItemMutation,
  useAddSafetyChecklistMutation,
  useDeleteSafetyChecklistItemMutation,
  useGetSafetyChecklistById,
  useGetSafetyChecklistItemList,
  useUpdateSafetyChecklistMutation,
  useUpdateSafetyChecklistItemMutation,
} from "@/services";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useEffect, useState } from "react";

const useAddEdit = () => {
  const { id, action, tabRowId, tabRowAction } = useParams();
  const isViewCategory = tabRowAction == "view";
  const isViewProject = action == "view";
  const [deletedItemIds, setDeletedItemIds] = useState<string[]>([])
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = action == "edit";
  const isView = isViewCategory || isViewProject;

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [TITLE]: "",
      [CHECKLIST_ITEM]: [{[NAME]: "", [IMPORTANCE_FLAG]: ""}],
      [DESCRIPTION]: "",
    },
  });

  const projectData = useSelector(
    (state: RootState) => state.currentData.projectData
  );

  const requiredFields = getRequiredFields(schema);

  const navigateUrl = id
    ? `/${routes.admin}/${routes.projectOverview}/${id}/${action}/${routes.safetyChecklist}`
    : `/${routes.admin}/${routes.projectOverview}/${routes.add}/${routes.safetyChecklist}`;

  const breadcrumbData = [
    {
      link: navigateUrl,
      name: `Checklist for Safety`,
    },
    {
      link: "",
      name: tabRowAction ? capitalize(tabRowAction || "") : "Add",
    },
  ];

  const title = tabRowId ? `${capitalize(tabRowAction || '')} Checklist for Safety` : 'Add Checklist for Safety'

  const onBackClick = () => {
    navigate(navigateUrl);
  };

  const {data: checklistData, isLoading: isLoadingChecklistData} = useGetSafetyChecklistById({
    id: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })

  const {data: checklistItems, isLoading: isLoadingChecklistItems} = useGetSafetyChecklistItemList({
    checklistId: tabRowId as string,
    queryParams: {
      enabled: !!tabRowId
    }
  })

  useEffect(() => {
    if (checklistData?.data) {
      form.setValue(TITLE, checklistData?.data?.[TITLE] || '')
      form.setValue(DESCRIPTION, checklistData?.data?.[DESCRIPTION] || '')
    }
  }, [checklistData]);

  useEffect(() => {
    if (checklistItems?.data) {
      form.setValue(CHECKLIST_ITEM, checklistItems?.data)
    }
  }, [checklistItems])
  
  const { mutateAsync: addItemInfo, isPending: isAddItemPending } =
    useAddSafetyChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutateAsync: updateItemInfo, isPending: isUpdateItemPending } =
    useUpdateSafetyChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutateAsync: deleteItemInfo, isPending: isDeleteItemPending } =
    useDeleteSafetyChecklistItemMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
    });

  const { mutate: addInfo, isPending: isAddPending } =
    useAddSafetyChecklistMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(CHECKLIST_ITEM)?.map((val: any) => {
            additionalPromises.push(
              addItemInfo({
                checklistId: data?.data?.data?.taskSafetyChecklistId,
                data: {
                  ...val,
                },
              })
            );
          });

          await Promise.all(additionalPromises);

          toast({
            message: "Checklist Added Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["safety-checklist", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateSafetyChecklistMutation({
      onError: (error?: any) => {
        toast({
          message:
            typeof error?.response?.data?.message === "string"
              ? error?.response?.data?.message
              : "Something went wrong",
          type: "error",
        });
        return error?.response?.data?.message;
      },
      onSuccess: async (data: any) => {
        try {
          const additionalPromises: Promise<any>[] = [];

          form.watch(CHECKLIST_ITEM)?.map((val: any) => {
            if (!val.taskSafetyChecklistItemId) {
              additionalPromises.push(
                addItemInfo({
                  checklistId: data?.data?.data?.taskSafetyChecklistId,
                  data: {
                    ...val,
                  },
                })
              );
            } else {
              additionalPromises.push(
                updateItemInfo({
                  checklistId: data?.data?.data?.taskSafetyChecklistId,
                  itemId: val.taskSafetyChecklistItemId,
                  data: {
                    name: val.name,
                    importanceFlag: val.importanceFlag,
                  },
                })
              );
            }
          });

          deletedItemIds?.map((val: any) => {
            additionalPromises.push(
              deleteItemInfo({id: val, checklistId: tabRowId as string})
            )
          })

          await Promise.all(additionalPromises);

          toast({
            message: "Checklist Updated Successfully",
            type: "success",
          });

          queryClient.invalidateQueries({
            queryKey: ["safety-checklist", projectData?.projectId],
          });
          onBackClick();
        } catch (error) {
          console.error("Error in post-success APIs:", error);
          toast({
            message: "Something went wrong while processing additional data.",
            type: "error",
          });
        }
      },
    });

  const onSubmit = (data: formValues) => {
    let _data = setEmptyFieldsToNull(data);
    _data = removeEmptyFields(_data);

    if (tabRowId) {
      updateInfo({ id: tabRowId, data: { ..._data, projectId: projectData?.projectId } });
    } else {
      addInfo({ data: { ..._data, projectId: projectData?.projectId } });
    }
  };

  const onAddMoreTask = () => {
    form.clearErrors();
    form.setValue(CHECKLIST_ITEM, [...form.getValues()[CHECKLIST_ITEM], {[NAME]: "", [IMPORTANCE_FLAG]: ""}]);
  };

  const onDeleteTask = (idx: number) => () => {
    const taskSafetyChecklistItemId = (form.getValues()[CHECKLIST_ITEM][idx] as any)?.taskSafetyChecklistItemId

    if (taskSafetyChecklistItemId) {
      setDeletedItemIds((prev: any) => [...prev, taskSafetyChecklistItemId])
    }

    form.setValue(CHECKLIST_ITEM, [
      ...form
        .getValues()
        [CHECKLIST_ITEM]?.filter((_: any, index: number) => idx != index),
    ]);
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    onAddMoreTask,
    onDeleteTask,
    isEdit,
    isAddItemPending: isAddItemPending || isUpdateItemPending,
    isView,
    title,
    isUpdatePending,
    isDeleteItemPending,
    isLoadingChecklistData,
    isLoadingChecklistItems 
  };
};

export default useAddEdit;
